import { registerPlugin } from '@capacitor/core';

export interface PaywallShowOptions {
    /** Store product id for the current pricing tier (from paywall-data). */
    productId?: string;
    /** Refer-a-friend discounted product variant for the current tier. */
    referralProductId?: string;
    /**
     * Validated refer-a-friend code. Android sends it with backend validation;
     * iOS shows its own code entry in the native sheet instead.
     */
    referralCode?: string;
    /** Referral discount % — display-only hint for the native iOS sheet. */
    referralDiscountPercent?: number;
}

export interface PaywallShowResult {
    success: boolean;
    cancelled?: boolean;
    error?: string;
}

export interface PaywallRestoreResult {
    restored: boolean;
    error?: string;
}

export interface PaywallEntitlementResult {
    entitled: boolean;
}

export interface PaywallProductDetailsResult {
    available: boolean;
    /** Localized, formatted store price (e.g. "€30.00"). Only set when available. */
    price?: string;
    /** Why no price came back — only set when available is false. */
    error?: string;
}

export interface PaywallCapabilities {
    /**
     * True when the native binary honors PaywallShowOptions (tiered +
     * refer-a-friend products). Old binaries don't implement this method at
     * all — callers must treat a rejection as { dynamicProducts: false },
     * since OTA web updates run inside older binaries.
     */
    dynamicProducts: boolean;
}

interface PaywallPluginInterface {
    showPaywall(options?: PaywallShowOptions): Promise<PaywallShowResult>;
    restorePurchases(): Promise<PaywallRestoreResult>;
    checkEntitlement(): Promise<PaywallEntitlementResult>;
    /** Android only — iOS uses the fully native paywall sheet instead. */
    getProductDetails(options?: { productId?: string }): Promise<PaywallProductDetailsResult>;
    /** Not implemented on pre-tiered-pricing binaries — always catch. */
    getCapabilities(): Promise<PaywallCapabilities>;
}

export const PaywallPlugin = registerPlugin<PaywallPluginInterface>('PaywallPlugin');
